// Browser port of POSWeb.Domain.Pricing.PricingRuleEvaluator — StoreBuilder's
// Product.GetApplicablePricingRules semantics. Both implementations run the shared
// fixtures in webpos/pricing-rule-fixtures.json; change them together.
//
// Deliberate parity (not bugs): one rule only, highest priority wins, no stacking, no
// specificity; StartDate <= now && EndDate > now (the "to" day is excluded); minQoh is a
// stock threshold walked in priority order and skipped when stock is unknown; no rounding;
// no zero/negative guard. POS-defined: deterministic tie-break (receivedOn desc,
// externalId asc, sequence asc), case-insensitive matching, null list = every list.

const norm = (v) => String(v ?? '').trim().toLowerCase();

export function isNoList(list) {
  const n = norm(list);
  return n === '' || n === 'none';
}

export function listMatches(ruleList, productList) {
  if (isNoList(ruleList)) return true;
  if (isNoList(productList)) return false;
  return norm(ruleList) === norm(productList);
}

export function isLive(rule, nowMs, inclusiveEndDate = false) {
  if (!rule || rule.isActive === false) return false;
  const start = Date.parse(rule.startsAt);
  const end = Date.parse(rule.endsAt);
  if (Number.isFinite(start) && start > nowMs) return false;
  if (!Number.isFinite(end)) return true;
  return inclusiveEndDate ? end >= nowMs : end > nowMs;
}

function selectorMatches(rule, product) {
  // appliesTo is the message-level item_type; selectorKind is what the value really is.
  // Only rows where they agree can match (StoreBuilder pairs AppliesTo with one id column).
  if ((rule.selectorKind || rule.appliesTo) !== rule.appliesTo) return false;
  const sel = norm(rule.selector);
  if (!sel) return false;
  switch (rule.appliesTo) {
    case 'Variant':
      return !!product.sku && norm(product.sku) === sel;
    case 'Brand':
      return !!product.brand && norm(product.brand) === sel;
    case 'Category':
      return Array.isArray(product.categories) && product.categories.some(c => c && norm(c) === sel);
    default:
      return false; // Product-scoped rules are inert in StoreBuilder.
  }
}

export function computeSalePrice(price, rule) {
  const p = Number(price) || 0;
  switch (rule.ruleType) {
    case 'DiscountValue': return p - (Number(rule.amount) || 0);
    case 'Percentage': return p * (1 - (Number(rule.pct) || 0));
    case 'Price': return Number(rule.amount) || 0;
    default: return p;
  }
}

function compareRules(a, b) {
  if (b.priority !== a.priority) return (b.priority || 0) - (a.priority || 0);
  const ra = Date.parse(a.receivedOn) || 0;
  const rb = Date.parse(b.receivedOn) || 0;
  if (rb !== ra) return rb - ra;
  const ea = String(a.externalId ?? '');
  const eb = String(b.externalId ?? '');
  if (ea !== eb) return ea < eb ? -1 : 1;
  return (a.sequence || 0) - (b.sequence || 0);
}

/**
 * @param {{sku:string, brand?:string, categories?:string[], priceList?:string, price:number, qoh?:number|null}} product
 * @param {Array} rules rows in the pricingrules file shape
 * @param {number} nowMs
 * @param {{inclusiveEndDate?:boolean}} opts
 * @returns {{match: object|null, candidates: Array}}
 */
export function explain(product, rules, nowMs = Date.now(), opts = {}) {
  const candidates = [];
  if (!product || !Array.isArray(rules)) return { match: null, candidates };
  const inclusive = !!opts.inclusiveEndDate;
  const qohKnown = product.qoh !== null && product.qoh !== undefined && Number.isFinite(Number(product.qoh));
  const qoh = qohKnown ? Number(product.qoh) : null;

  const ordered = rules
    .filter(r => isLive(r, nowMs, inclusive))
    .filter(r => listMatches(r.priceList, product.priceList))
    .filter(r => selectorMatches(r, product))
    .sort(compareRules);

  let selected = null;
  for (const r of ordered) {
    const thresholdOk = !qohKnown || qoh >= (Number(r.minQoh) || 0);
    const take = selected === null && thresholdOk;
    if (take) selected = r;
    candidates.push({
      externalId: r.externalId, selector: r.selector, appliesTo: r.appliesTo, priority: r.priority,
      minQoh: r.minQoh || 0, selected: take,
      reason: take ? null : (!thresholdOk ? `stock ${qoh} below minimum ${r.minQoh}` : 'outranked'),
    });
  }
  if (!selected) return { match: null, candidates };

  return {
    match: {
      externalId: selected.externalId,
      priceList: selected.priceList ?? null,
      appliesTo: selected.appliesTo,
      selector: selected.selector,
      ruleType: selected.ruleType,
      value: selected.ruleType === 'Percentage' ? (Number(selected.pct) || 0) : (Number(selected.amount) || 0),
      priority: selected.priority,
      salePrice: computeSalePrice(product.price, selected),
      startsAt: selected.startsAt,
      endsAt: selected.endsAt,
    },
    candidates,
  };
}

export function evaluate(product, rules, nowMs = Date.now(), opts = {}) {
  return explain(product, rules, nowMs, opts).match;
}

/** "−50%", "−$30.00" or "$55.00 fixed" */
export function describeRule(match) {
  if (!match) return '';
  const v = Number(match.value) || 0;
  switch (match.ruleType) {
    case 'Percentage': return `−${Math.round(v * 10000) / 100}%`;
    case 'DiscountValue': return `−$${v.toFixed(2)}`;
    case 'Price': return `$${v.toFixed(2)} fixed`;
    default: return '';
  }
}

/** The real cut-off instant in the terminal's local time (the "to" day itself is excluded). */
export function formatEndsAt(endsAt) {
  const ms = Date.parse(endsAt);
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  if (d.getFullYear() >= 9999) return 'no end date';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Discount percentage (2 dp) that turns originalPrice into rulePrice through the cart's discount path. */
export function discountPercentFor(originalPrice, rulePrice) {
  const o = Number(originalPrice) || 0;
  if (o <= 0) return 0;
  const pct = (1 - Number(rulePrice) / o) * 100;
  return Math.round(pct * 100) / 100;
}
